import React, { useMemo, useEffect } from 'react';
import { AIRecommendationsWidget } from './AIRecommendationsWidget';
import { useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { useSetAtom } from 'jotai';
import { GET_AI_RECOMMENDATIONS } from '../../../../graphql/queries/portfolio';
import { GET_INSTRUMENTS_BY_TICKERS } from '../../../../graphql/queries/instruments';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  aiReasoningMapAtom,
  type AIReasoningEntry,
} from '~/atoms/dashboardAtom';

interface AIRecommendation {
  action: 'REDUCE' | 'INCREASE' | 'MAINTAIN';
  reason: string;
  ai_reasoning?: string;
  ticker: string;
  confidence: 'high' | 'medium' | 'low';
  current_allocation: number;
  recommended_allocation: number;
}

interface Instrument {
  id: string;
  ticker: string;
  full_ticker: string;
  name: string;
  isin: string;
  sector: string;
  currency: string;
  exchange: string;
  companyId: string;
}

interface AIAnalysisData {
  recommendations: AIRecommendation[];
  recommendation_report?: string | null;
  risk_alerts?: Array<{
    title: string;
    message: string;
    level: 'low' | 'medium' | 'high';
    confidence: number;
  }>;
}

interface AIRecommendationsData {
  EQPortfoliosAIAnalysis: {
    analysis_data: AIAnalysisData;
    createdAt?: string;
  }[];
}

export interface AIRecommendationsWidgetWrapperProps {
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
}

export const AIRecommendationsWidgetWrapper: React.FC<
  AIRecommendationsWidgetWrapperProps
> = ({ widgetId, handleRemoveWidget, shortDescription }) => {
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const setAIReasoningMap = useSetAtom(aiReasoningMapAtom);
  const snapshotId = latestSnapshotId;

  const { data, loading, error, refetch } = useQuery<AIRecommendationsData>(
    GET_AI_RECOMMENDATIONS,
    {
      variables: { snapshotId },
      notifyOnNetworkStatusChange: true,
      skip: !snapshotId,
    },
  );

  // Extract tickers from recommendations
  const tickers = useMemo(() => {
    if (!data?.EQPortfoliosAIAnalysis?.[0]?.analysis_data?.recommendations)
      return [];
    return data.EQPortfoliosAIAnalysis[0].analysis_data.recommendations.map(
      (rec) => rec.ticker,
    );
  }, [data]);

  // Prepare variables for single query (array of tickers)
  const instrumentVariables = useMemo(() => {
    return {
      tickers: tickers, // Allow up to 10 tickers
    };
  }, [tickers]);

  // Single query for all tickers - only execute when we have valid tickers
  const shouldSkipQuery =
    tickers.length === 0 ||
    !tickers.some((ticker) => ticker && ticker.trim() !== '');

  const { data: instrumentsData, loading: instrumentsLoading } = useQuery(
    GET_INSTRUMENTS_BY_TICKERS,
    {
      variables: instrumentVariables,
      skip: shouldSkipQuery,
      notifyOnNetworkStatusChange: true,
    },
  );

  const recommendationsData = useMemo(() => {
    if (loading || !data?.EQPortfoliosAIAnalysis?.[0]?.analysis_data) return [];
    const analysisData = data.EQPortfoliosAIAnalysis[0].analysis_data;
    const instruments = instrumentsData?.EQInstrument || [];

    return analysisData.recommendations.map((rec: AIRecommendation) => {
      // Find matching instrument details
      const instrument = instruments.find((inst: Instrument) =>
        inst.full_ticker.startsWith(rec.ticker),
      );

      return {
        action: rec.action,
        ticker: rec.ticker,
        currentAllocation: `${rec.current_allocation}%`,
        proposedAllocation: `${rec.recommended_allocation}%`,
        reason: rec.reason,
        ai_reasoning: rec.ai_reasoning,
        confidence: (rec.confidence.charAt(0).toUpperCase() +
          rec.confidence.slice(1)) as 'High' | 'Medium' | 'Low',
        // Add company name if available
        companyName: instrument?.name || null,
        instrumentTicker: instrument?.full_ticker || null,
      };
    });
  }, [data, loading, instrumentsData]);

  useEffect(() => {
    if (!recommendationsData || recommendationsData.length === 0) {
      setAIReasoningMap({});
      return;
    }

    const addEntryForKey = (
      acc: Partial<Record<string, AIReasoningEntry>>,
      key?: string | null,
      entry?: AIReasoningEntry,
    ) => {
      if (!key || !entry) return;
      const normalizedKey = key.toUpperCase();
      if (!normalizedKey) return;
      acc[normalizedKey] = entry;

      const alternativeKeys = new Set<string>();
      const splitDelimiters = [':', '.', ' '];
      splitDelimiters.forEach((delimiter) => {
        if (normalizedKey.includes(delimiter)) {
          const truncated = normalizedKey.split(delimiter)[0];
          if (truncated) {
            alternativeKeys.add(truncated);
          }
        }
      });

      const alphanumericOnly = normalizedKey.replace(/[^A-Z0-9]/g, '');
      if (alphanumericOnly) {
        alternativeKeys.add(alphanumericOnly);
      }

      alternativeKeys.forEach((altKey) => {
        if (altKey) {
          acc[altKey] = entry;
        }
      });
    };

    const reasoningMap = recommendationsData.reduce<
      Partial<Record<string, AIReasoningEntry>>
    >((acc, rec) => {
      const entry = {
        reason: rec.reason,
        aiReasoning: rec.ai_reasoning,
        companyName: rec.companyName,
      };
      addEntryForKey(acc, rec.ticker, entry);
      addEntryForKey(acc, rec.instrumentTicker, entry);
      return acc;
    }, {});

    setAIReasoningMap(reasoningMap);
  }, [recommendationsData, setAIReasoningMap]);

  useEffect(() => {
    if (snapshotId) {
      refetch();
    }
  }, [snapshotId, refetch]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      refetch();
    }
  }, [triggerHardRefetch, refetch]);

  return (
    <AIRecommendationsWidget
      data={recommendationsData}
      isLoading={loading || instrumentsLoading}
      error={error || null}
      refetch={refetch}
      widgetId={widgetId}
      handleRemoveWidget={handleRemoveWidget}
      title={shortDescription}
      snapshotId={snapshotId}
    />
  );
};
